import { useEffect } from 'react';
import { Modal, Form, Input, Select, Tag, Alert, Button, message } from '../../ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { request } from '../../api/client';

interface Props {
  open: boolean;
  onClose: () => void;
  territory: any;
}

/**
 * Split a territory: create a new sibling (same parent + level) and move a chosen set of
 * its retailers into it. The new territory inherits the source's SP bindings, so moved
 * customers keep an owner. (Farmers can be moved afterwards via "Change territory".)
 */
export function SplitTerritoryModal({ open, onClose, territory }: Props) {
  const [form] = Form.useForm();
  const qc = useQueryClient();

  const { data: retailers } = useQuery({
    queryKey: ['retailers-in-territory', territory?.id],
    queryFn: () => request<any>({ url: '/retailers', params: { territoryId: String(territory.id), pageSize: 500 } }),
    enabled: open && !!territory?.id,
  });

  useEffect(() => { if (open) form.resetFields(); }, [open, form]);

  const split = useMutation({
    mutationFn: (v: any) => request({
      url: '/territory-assignments/split', method: 'POST',
      data: { sourceTerritoryId: String(territory.id), newCode: v.newCode?.trim(), newName: v.newName?.trim(), retailerIds: v.retailerIds ?? [] },
    }),
    onSuccess: (res: any) => {
      message.success(`Split done — ${res?.movedRetailers ?? 0} retailer(s) moved to the new territory`);
      qc.invalidateQueries({ queryKey: ['territories-tree'] });
      qc.invalidateQueries({ queryKey: ['territory-coverage'] });
      qc.invalidateQueries({ queryKey: ['retailers'] });
      qc.invalidateQueries({ queryKey: ['badges'] });
      onClose();
    },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Failed to split'),
  });

  return (
    <Modal open={open} onOpenChange={(o) => { if (!o) onClose(); }} title={territory ? `Split ${territory.code} · ${territory.name}` : 'Split territory'}
      footer={<><Button variant="default" onClick={onClose}>Cancel</Button><Button variant="primary" loading={split.isPending} onClick={() => form.submit()}>Split</Button></>}>
      {territory && (
        <Form form={form} layout="vertical" onFinish={(v) => split.mutate(v)}>
          <div style={{ marginBottom: 12 }}>New <Tag>{territory.level}</Tag> under the same parent as <b>{territory.code}</b>.</div>
          <Form.Item name="newCode" label="New code" rules={[{ required: true, message: 'A unique code is required' }]}>
            <Input placeholder={`${territory.code}-B`} />
          </Form.Item>
          <Form.Item name="newName" label="New name" rules={[{ required: true, message: 'Name is required' }]}>
            <Input placeholder={`${territory.name} (split)`} />
          </Form.Item>
          <Form.Item name="retailerIds" label="Retailers to move to the new territory"
            extra="Owner follows the new territory's primary SP (same as the source).">
            <Select mode="multiple" allowClear showSearch optionFilterProp="label" placeholder="Pick retailers (optional)"
              options={(retailers?.data ?? []).map((r: any) => ({ value: String(r.id), label: `${r.code} · ${r.companyName}` }))} />
          </Form.Item>
          <Alert type="info" title="The new territory starts with the same Sales Person bindings as the source. Nothing is deleted." />
        </Form>
      )}
    </Modal>
  );
}
